import React, { useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Alert, AlertTitle } from "@material-ui/lab";
import LockIcon from "@material-ui/icons/Lock";

import { clientesApi } from "../../services/clientesApi";

export default function ConfigurarCuentaCliente(props) {
  const { open, setOpen } = props;
  const { enqueueSnackbar } = useSnackbar();

  // Obtener datos del cliente autenticado
  const { cliente: user } = useSelector((state) => state.auth.user);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);

  // RTK Query mutation para editar clave
  const [editarClaveCliente] = clientesApi.useEditarClaveClienteMutation();

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  /**
   * Validaciones del formulario
   */
  const LoginSchema = Yup.object().shape({
    nuevaContrasena: Yup.string()
      .min(8, "La contraseña debe tener mínimo 8 caracteres")
      .required("Debe ingresar una nueva contraseña"),
    confirmarNuevaContrasena: Yup.string()
      .oneOf([Yup.ref("nuevaContrasena"), null], "Las contraseñas no coinciden")
      .required("Debe confirmar la nueva contraseña"),
  });

  const formik = useFormik({
    initialValues: {
      nuevaContrasena: "",
      confirmarNuevaContrasena: "",
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      guardarDatos();
    },
  });

  const {
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    resetForm,
  } = formik;

  /**
   * Guardar nueva contraseña usando RTK Query
   */
  const guardarDatos = useCallback(async () => {
    try {
      await editarClaveCliente({
        refCliente: user.rut,
        clave: getFieldProps("nuevaContrasena").value,
        institucion: "SITAX",
        nombreUsuario: user.rut,
        refSucursal: "CL", // Los clientes usan sucursal "CL"
      }).unwrap();

      enqueueSnackbar("Contraseña actualizada correctamente", {
        variant: "success",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });

      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error al actualizar la contraseña:", error);
      enqueueSnackbar(
        error?.data?.message ||
          error.message ||
          "Ocurrió un error al actualizar la contraseña",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  }, [
    editarClaveCliente,
    enqueueSnackbar,
    getFieldProps,
    handleClose,
    resetForm,
    user.rut,
  ]);

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleShowConfirmPassword = () => {
    setConfirmShowPassword((prev) => !prev);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Cambiar Contraseña del Portal
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Actualiza la contraseña de acceso a tu portal de clientes
          </DialogContentText>

          {/* Alert de información sobre encriptación */}
          <Alert
            severity="info"
            icon={<LockIcon />}
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            <AlertTitle>Encriptación Automática</AlertTitle>
            Tu nueva contraseña será encriptada con <strong>
              AES-256-GCM
            </strong>{" "}
            para garantizar la seguridad de tus credenciales.
          </Alert>

          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <TextField
                required
                fullWidth
                autoFocus
                autoComplete="new-password"
                type={showPassword ? "text" : "password"}
                label="Nueva Contraseña"
                margin="normal"
                {...getFieldProps("nuevaContrasena")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(
                  touched.nuevaContrasena && errors.nuevaContrasena,
                )}
                helperText={touched.nuevaContrasena && errors.nuevaContrasena}
              />

              <TextField
                required
                fullWidth
                autoComplete="new-password"
                type={showConfirmPassword ? "text" : "password"}
                label="Confirmar Nueva Contraseña"
                margin="normal"
                {...getFieldProps("confirmarNuevaContrasena")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleShowConfirmPassword}
                        edge="end"
                      >
                        <Icon
                          icon={showConfirmPassword ? eyeFill : eyeOffFill}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(
                  touched.confirmarNuevaContrasena &&
                  errors.confirmarNuevaContrasena,
                )}
                helperText={
                  touched.confirmarNuevaContrasena &&
                  errors.confirmarNuevaContrasena
                }
              />

              <DialogActions style={{ marginTop: 16 }}>
                <LoadingButton onClick={handleClose} color="error">
                  Cancelar
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  color="primary"
                  startIcon={<Icon icon={saveFill} />}
                  loading={isSubmitting}
                >
                  Actualizar Contraseña
                </LoadingButton>
              </DialogActions>
            </Form>
          </FormikProvider>
        </DialogContent>
      </Dialog>
    </>
  );
}
